import React, { memo, useCallback, useMemo } from 'react'

import { useAppDispatch } from 'hooks/reduxHooks'
import styled from 'styled-components'
import { rem } from 'polished'

import { DEFAULT_MAX_TOUR_V2_VARIATIONS_TO_SHOW, OfferPageActions, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { getVariationsDepartureBoundaries, getTourV2OfferPageBaseUrl } from 'selectors/TourV2/tourV2OfferPageSelectors'
import { pushWithRegion } from 'actions/NavigationActions'
import { sortBy } from 'lib/arrayUtils'
import findCheapestTourV2PurchasableOption from 'lib/TourV2/findCheapestTourV2PurchasableOption'
import SlideDown from 'components/Common/SlideDown'
import TextButton from 'components/Luxkit/Button/TextButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'

import TourV2VariationCard from './TourV2VariationCard'
import { tourV2OfferVariationView } from 'actions/AnalyticsActions'
import { mediaQueryUp } from 'components/utils/breakpoint'
import cn from 'clsx'
import config from 'constants/config'

const VariationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  ${mediaQueryUp.md} {
    flex-direction: row;
  }

  &.vertical-view {
    ${mediaQueryUp.md} {
      flex-direction: column;
    }
  }

  &.top {
    margin-top: ${rem(16)};
  }
`

const VariationsButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${rem(24)};
`

interface MappedProps {
  baseTourOfferUrl: string
  offer: Tours.V2Offer
  selectedVariation: Tours.V2OfferVariation
  variations: Record<string, Tours.V2OfferVariation>
  variationsDepartureBoundaries: Tours.V2OfferVariationsDepartureBoundaries
  variationsToShow: number
}

interface Props {
  onVariationChanged: (variationId: string) => void
}

function TourV2Variations(props: Props & MappedProps) {
  const {
    baseTourOfferUrl,
    offer,
    selectedVariation,
    variations,
    variationsDepartureBoundaries,
    variationsToShow,
    onVariationChanged,
  } = props

  const offerPageDispatch = useOfferPageDispatch()

  const dispatch = useAppDispatch()

  const selectVariationId = useCallback(
    (variationId: Tours.V2OfferVariation['id']) => {
      if (selectedVariation.id !== variationId) {
        offerPageDispatch({
          type: OfferPageActions.unselectTourV2OfferDeparture,
        })
        dispatch(pushWithRegion(
          `${baseTourOfferUrl}/${variationId}`,
          '',
          { scrollBehaviour: 'smooth', scrollTo: '#TOUR_V2_CONTENT_TOP_ANCHOR' },
        ))
        dispatch(tourV2OfferVariationView(offer))
        offerPageDispatch({
          type: OfferPageActions.selectTourVOfferVariation,
          variation: offer.variations[variationId],
        })
        onVariationChanged(variationId)
      }
    },
    [selectedVariation, offerPageDispatch, dispatch, baseTourOfferUrl, onVariationChanged, offer],
  )

  const {
    initiallyVisibleVariations,
    restOfVariations,
  } = useMemo(() => {
    const sortedVariations = sortBy(
      Object.values(variations),
      (variation) => findCheapestTourV2PurchasableOption(offer, variation.id)?.price
      , 'asc',
    )

    return {
      initiallyVisibleVariations: sortedVariations.slice(0, DEFAULT_MAX_TOUR_V2_VARIATIONS_TO_SHOW),
      restOfVariations: sortedVariations.slice(DEFAULT_MAX_TOUR_V2_VARIATIONS_TO_SHOW),
    }
  }, [offer, variations])

  const hasMoreVariationsToShow = restOfVariations.length > 0
  const hasShownMorVariations = variationsToShow > DEFAULT_MAX_TOUR_V2_VARIATIONS_TO_SHOW

  const toggleMoreVariations = useCallback(() => {
    if (hasShownMorVariations) {
      offerPageDispatch({ type: OfferPageActions.resetMaxTourV2VariationsToShow })
    } else {
      offerPageDispatch({ type: OfferPageActions.showAllTourOfferVariations })
    }
  }, [hasShownMorVariations, offerPageDispatch])

  const sortedVariations = useMemo(() => {
    return sortBy(
      Object.values(variations),
      (variation) => findCheapestTourV2PurchasableOption(offer, variation.id)?.price
      , 'asc',
    )
  }, [offer, variations])

  const isPackageUpgradeEnabled = config.TOUR_V2_PACKAGE_UPGRADE

  return <>
    <VariationsList className={cn({ 'vertical-view': !isPackageUpgradeEnabled })}>
      {isPackageUpgradeEnabled && sortedVariations.map((variationOption, variationOptionIndex) =>
        <TourV2VariationCard
          key={variationOptionIndex + '_1'}
          position={variationOptionIndex + 1}
          earliestDeparture={variationsDepartureBoundaries[variationOption.id].earliestDeparture}
          latestDeparture={variationsDepartureBoundaries[variationOption.id].latestDeparture}
          offer={offer}
          variation={variationOption}
          isSelected={variationOption.id === selectedVariation.id}
          onVariationSelect={selectVariationId}
        />)}
      {!isPackageUpgradeEnabled && initiallyVisibleVariations.map((variationOption, variationOptionIndex) =>
        <TourV2VariationCard
          key={variationOptionIndex}
          position={variationOptionIndex + 1}
          earliestDeparture={variationsDepartureBoundaries[variationOption.id].earliestDeparture}
          latestDeparture={variationsDepartureBoundaries[variationOption.id].latestDeparture}
          offer={offer}
          variation={variationOption}
          isSelected={variationOption.id === selectedVariation.id}
          onVariationSelect={selectVariationId}
        />)}
    </VariationsList>
    {!isPackageUpgradeEnabled && hasMoreVariationsToShow && <SlideDown show={variationsToShow > DEFAULT_MAX_TOUR_V2_VARIATIONS_TO_SHOW}>
      <VariationsList className="vertical-view top">
        {restOfVariations.map((variationOption, variationOptionIndex) =>
          <TourV2VariationCard
            key={variationOptionIndex}
            position={variationOptionIndex + 1}
            earliestDeparture={variationsDepartureBoundaries[variationOption.id].earliestDeparture}
            latestDeparture={variationsDepartureBoundaries[variationOption.id].latestDeparture}
            offer={offer}
            variation={variationOption}
            isSelected={variationOption.id === selectedVariation.id}
            onVariationSelect={selectVariationId}
          />)}
      </VariationsList>
    </SlideDown>}
    {!isPackageUpgradeEnabled && hasMoreVariationsToShow && <VariationsButtonContainer>
      <TextButton kind="tertiary" onClick={toggleMoreVariations}>
        Show&nbsp;{variationsToShow > DEFAULT_MAX_TOUR_V2_VARIATIONS_TO_SHOW ? 'less' : 'more'}
      </TextButton>
    </VariationsButtonContainer>}
  </>
}

const mapOfferPageStateToProps = (offerPageState: OfferPageState): MappedProps => ({
  baseTourOfferUrl: getTourV2OfferPageBaseUrl(offerPageState),
  offer: offerPageState.tourV2Offer,
  selectedVariation: offerPageState.selectedTourV2OfferVariation,
  variations: offerPageState.tourV2Offer?.variations,
  variationsDepartureBoundaries: getVariationsDepartureBoundaries(offerPageState),
  variationsToShow: offerPageState.maxTourV2VariationsToShow,
})

export default memo(withOfferPageState(mapOfferPageStateToProps)(TourV2Variations))
